import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatOption} from '@angular/material/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {
    OldLoanApiService,
    ReferenceBookResponse,
    ReferenceBookType,
    SubjectApiService,
    SubjectType,
} from '@npck/af-frontend-api';
import {downloadFromBlob, getFormattedDateOrNull, rxSpinner, Spinner} from '@npck/libs/commons';
import {CalendarComponent, SnackbarService} from '@npck/libs/ui-kit';
import {combineLatest, debounceTime, distinctUntilChanged, filter, Observable, Subscription} from 'rxjs';
import {DateRangeForm} from '../../../components/date-range-picker-field/date-range-picker-field.component';
import {DynamicBooleanForm, FilterEvent} from '../../../components/full-table/filter/filter.component';
import {AuthHolderService} from '../../../domain/auth-holder.service';
import {DictionaryHolderService} from '../../../domain/dictionary-holder.service';
import {getDateTime} from '../../../infra/date-time.validator';

@Component({
    templateUrl: 'export-subject-file-dialog.component.html',
    styleUrls: ['export-subject-file-dialog.component.scss'],
    standalone: false
})
export class ExportSubjectFileDialogComponent implements OnInit, OnDestroy {
    @ViewChild('picker') dateRangePicker: CalendarComponent;
    @ViewChild('selectAll') selectAll: MatOption;

    Spinner = Spinner;

    form: FormGroup<ExportListForm>;
    subscription: Subscription;
    dateRangeSubscription: Subscription;
    subjectTypes: ReferenceBookResponse[];
    categories: ReferenceBookResponse[];
    subjectStatuses: ReferenceBookResponse[];

    constructor(
        @Inject(MAT_DIALOG_DATA) public dialogData: ExportFraudFileDialogData,
        public dialogRef: MatDialogRef<ExportSubjectFileDialogComponent>,
        private loanApiService: OldLoanApiService,
        private subjectApiService: SubjectApiService,
        private formBuilder: FormBuilder,
        private snackbarService: SnackbarService,
        private translateService: TranslateService,
        private authHolderService: AuthHolderService,
        private dictionaryHolderService: DictionaryHolderService,
    ) {
    }

    get isLoanRestricted(): boolean {
        return !this.authHolderService.hasAccess(['npck-admin']);
    }

    get organizationBin(): string {
        return this.authHolderService.organizationBin;
    }

    ngOnInit(): void {
        this.form = this.formBuilder.group({});
        if (this.dialogData.exportMode == 'subjects') {
            this.form.addControl('dateRangeForm', this.formBuilder.group({}));

            combineLatest([
                this.dictionaryHolderService.getReference('subject_type'),
                this.dictionaryHolderService.getReference(ReferenceBookType.CATEGORY),
                this.dictionaryHolderService.getReference('subject_status'),
            ])
                .pipe(rxSpinner('exporting'))
                .subscribe(([subjectTypes, categories, subjectStatuses]) => {
                    this.subjectTypes = subjectTypes;
                    const subjectTypeMultipleGroup = this.initMultipleSelectFormGroup(this.subjectTypes);
                    this.form.addControl('subjectTypeMultiple', subjectTypeMultipleGroup);

                    this.categories = categories;
                    const categoryMultipleGroup = this.initMultipleSelectFormGroup(this.categories);
                    this.form.addControl('categoryMultiple', categoryMultipleGroup);

                    this.subjectStatuses = subjectStatuses;
                    const subjectStatusMultipleGroup = this.initMultipleSelectFormGroup(this.subjectStatuses);
                    this.form.addControl('subjectStatusMultiple', subjectStatusMultipleGroup);
                });

            this.subscribeToDateRangeChanges();
        } else {
            const preselectedOrganizations = this.isLoanRestricted ? [this.organizationBin] : null;
            this.form.addControl('organizations', new FormControl(preselectedOrganizations, [Validators.required]));
        }
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
        this.dateRangeSubscription?.unsubscribe();
        Spinner.hideAll();
    }

    exportFraudList(): void {
        if (this.form.invalid) {
            return;
        }
        Spinner.show('exporting');
        const formValue = this.form.value;
        let request: Observable<Blob>;
        if (this.dialogData.exportMode === 'subjects') {
            const subjectStatuses = this.extractSelectedValues('subjectStatusMultiple');
            request = this.subjectApiService.exportSubjects({
                isDeleted: subjectStatuses.length === 1 ? (/true/i).test(subjectStatuses[0]) : null,
                dateFrom: getFormattedDateOrNull(formValue.dateRangeForm.dateFrom),
                dateTo: getFormattedDateOrNull(formValue.dateRangeForm.dateTo),
                category: this.extractSelectedValues('categoryMultiple'),
                subjectTypes: this.extractSelectedValues('subjectTypeMultiple') as SubjectType[],
                hasDeletedCategory: false,
                shouldHideVictim: false,
            });
        } else {
            request = this.loanApiService.exportFraudsterLoans(this.form.value.organizations);
        }
        const fileName = this.translateService.instant('i18n_af_report#' + this.dialogData.exportMode);
        this.subscription = request
            .subscribe({
                next: file => {
                    this.dialogRef.close();
                    downloadFromBlob(file, `${fileName}.xlsx`);
                    Spinner.hide('exporting');
                    this.snackbarService.showSnackBar('i18n_af_snackBarMessageFileSuccessfullyExported');
                }, error: err => {
                    Spinner.hide('exporting');
                    throw err;
                },
            });
    }

    initDateRangeField(): void {
        this.form.controls.dateRangeForm.setErrors(null);
        if (!this.form.controls.dateRangeForm.controls.dateFrom.value) {
            this.form.controls.dateRangeForm.controls.dateFrom.setValue(null);
        }
        if (!this.form.controls.dateRangeForm.controls.dateTo.value) {
            this.form.controls.dateRangeForm.controls.dateTo.setValue(null);
        }
    }

    subscribeToDateRangeChanges(): void {
        this.dateRangeSubscription?.unsubscribe();
        this.dateRangeSubscription = this.form.controls.dateRangeForm.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            filter(value => !!value.dateFrom && !!value.dateTo),
        ).subscribe(value => {
            const startDate = getDateTime(value.dateFrom.toISOString(), value.timeFrom);
            const endDate = getDateTime(value.dateTo.toISOString(), value.timeTo);

            const diff = endDate?.diff(startDate, 'month', true);
            if (diff > 3) {
                this.form.controls.dateRangeForm.setErrors({invalidMaxDateRange: true});
                this.form.controls.dateRangeForm.markAsTouched();
            }
        });
    }

    selectedValueCount(formGroup: string): number {
        if (this.form.contains(formGroup)) {
            const controls = (this.form.get(formGroup) as FormGroup)?.controls;
            if (!controls) {
                return 0;
            }
            return Object.entries(controls)
                .filter(([_, control]) => (control.value === true || control.value === 'true'))
                .length;
        }

        return 0;
    }

    private initMultipleSelectFormGroup(values: ReferenceBookResponse[]): FormGroup {
        const formGroup = this.formBuilder.group({});
        values.forEach(value => {
            formGroup.addControl(value.code, new FormControl(false));
        });
        return formGroup;
    }

    private extractSelectedValues(formGroupName: string): string[] {
        const controls = (this.form.get(formGroupName) as FormGroup)?.controls;
        return Object.entries(controls)
            .filter(([_, control]) => (control.value === true || control.value === 'true'))
            .map(([key, _]) => key);
    }

}

export declare type ImportExportMode = 'subjects' | 'loans';

export interface ExportListForm {
    dateRangeForm?: FormGroup<DateRangeForm>
    categoryMultiple?: FormGroup<DynamicBooleanForm>;
    subjectTypeMultiple?: FormGroup<DynamicBooleanForm>;
    subjectStatusMultiple?: FormGroup<DynamicBooleanForm>;
    organizations?: FormControl<string[]>;
}

export interface ExportFraudFileDialogData {
    exportMode: ImportExportMode;
    filter: FilterEvent;
}
